// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ActionPopup.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * @module Action popup component
 */

import { filter, flow, get, head, invokeArgs, isEmpty, isNil, lowerCase } from 'lodash/fp';
import DashboardConstants from 'logic/data/Izenda.DashboardConstants';
import UIConstants from 'logic/data/Izenda.UIConstants';
import appStore from 'logic/Izenda.AppStore';
import uiState from 'logic/Izenda.UiState';
import { action, computed, observable } from 'mobx';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import Input from 'share/components/controls/Izenda.Input';
import Label from 'share/components/controls/Izenda.Label';
import Select from 'share/components/controls/multiSelectBox/Izenda.Select';
import Validator from 'share/components/hoc/Izenda.Validator';
import connect from 'share/enhancers/Izenda.Connect';

const ValidatableInput = Validator()(Input);
const CANCEL_TEXT = 'cancel';
const ACCEPT_TEXT = 'accept';

@connect()
export default class ActionPopup extends Component {
  static propTypes = {
    dashboard: PropTypes.object,
    categories: PropTypes.object,
    onClose: PropTypes.func,
    popupConfig: PropTypes.object,
    action: PropTypes.number,
    actionFunc: PropTypes.func
  };

  @observable name;
  @observable selectedSaveInto;
  @observable categoryId;
  @observable category;
  @observable subCategoryId;
  @observable subCategory;
  @observable categories;
  @observable isLastPageCategorySearch;
  @observable subCategories;
  @observable isLastPageSubCategorySearch;

  constructor(props: any) {
    super(props);

    const {
      dashboard: { name }
    } = props;

    this.name = name;
    this.selectedSaveInto = null;
    this.categoryId = null;
    this.category = null;
    this.subCategoryId = null;
    this.subCategory = null;
    this.categories = [];
    this.isLastPageCategorySearch = false;
    this.subCategories = [];
    this.isLastPageSubCategorySearch = false;
  }

  /**
   * Get status of subcategory control
   */
  @computed
  get disableSubCategory() {
    return isNil(this.categoryId);
  }

  componentDidMount() {
    appStore.loadCategorySettings().then(
      action((result: any) => {
        // The value of Save Into is 'local' if the system / user is tenant level
        const isGlobal =
          uiState.currentUser.systemAdmin && isNil(uiState.tenantId) ? this.props.dashboard.isGlobal : false;
        this.localGlobalCategories = result;
        this.selectedSaveInto = get('id', this.getSaveInCategory(isGlobal, result));
      })
    );
  }

  /**
   * Get first global category
   * @param {boolean} isGlobal
   * @param {Array<Object>} localGlobalCategories
   */
  getSaveInCategory(isGlobal: boolean, localGlobalCategories: Array<any>) {
    return flow(
      filter({ isGlobal: isGlobal }),
      head
    )(localGlobalCategories);
  }

  /**
   * Fetch categories
   * @param {Object} pageInfo
   */
  getCategories = (pageInfo: any) => {
    pageInfo = pageInfo || {
      pageIndex: 1,
      pageSize: UIConstants.DEFAUFT_DROPDOWN_ITEMS * UIConstants.LAZY_LOADING_DEFAULT_PAGE_INDEX
    };
    const request = {
      pageIndex: pageInfo.pageIndex,
      pageSize: pageInfo.pageSize,
      type: 2,
      criteria: [{ key: 'name', value: this.searchCategoryKey }],
      parentIds: [this.selectedSaveInto],
      tenantId: uiState.tenantId,
      includeUncategory: false
    };

    return invokeArgs('getCategoriesFunc', [request], this.props).then(
      action((data: any) => {
        this.categories = data.categories;
        this.isLastPageCategorySearch = data.isLastPage;
        return data.categories;
      })
    );
  };

  /**
   * On search category
   * @param {string} key
   */
  handleSearchCategoryData = (key: string) => {
    this.searchCategoryKey = key;
    return this.getCategories(null);
  };

  /**
   * Reset category data source
   */
  resetCategory = () => {
    this.searchCategoryKey = null;
    this.categories = [];
  };

  /**
   * Fetch sub-categories
   * @param {Object} pageInfo
   */
  getSubCategories = (pageInfo: any) => {
    if (String.isGuidFormat(this.categoryId)) {
      pageInfo = pageInfo || {
        pageIndex: 1,
        pageSize: UIConstants.DEFAUFT_DROPDOWN_ITEMS * UIConstants.LAZY_LOADING_DEFAULT_PAGE_INDEX
      };
      const request = {
        pageIndex: pageInfo.pageIndex,
        pageSize: pageInfo.pageSize,
        type: 2,
        criteria: [{ key: 'name', value: this.searchSubCategoryKey }],
        parentIds: [this.selectedSaveInto, this.categoryId],
        tenantId: uiState.tenantId
      };

      return invokeArgs('getSubcategoriesFunc', [request], this.props).then(
        action((data: any) => {
          this.subCategories = data.subCategories;
          this.isLastPageSubCategorySearch = data.isLastPage;
          return data.subCategories;
        })
      );
    } else {
      return Promise.resolve([]);
    }
  };

  /**
   * On search sub-category
   * @param {string} key
   */
  handleSearchSubCategoryData = (key: string) => {
    this.searchSubCategoryKey = key;
    return this.getSubCategories(null);
  };

  /**
   * Reset sub-category data source
   */
  resetSubCategory = () => {
    this.searchSubCategoryKey = null;
    this.subCategories = [];
  };

  /**
   * Handle when click ok button
   */
  onAcceptClosing = () => {
    const props = this.props;
    const { validation: valManager, dashboard, action } = props;
    let formatMessage =
      action === DashboardConstants.COPY_KEY ? DashboardConstants.MESSAGE.M06b : DashboardConstants.MESSAGE.M06a;

    const successMessage = String.format(
      formatMessage,
      this.name,
      !isEmpty(this.category) ? this.category : DashboardConstants.UNCATEGORIZED,
      !isEmpty(this.subCategory) ? '/' + this.subCategory : ''
    );

    const data = {
      id: dashboard.id,
      name: this.name,
      isGlobal: flow(
        filter({ id: this.selectedSaveInto }),
        head,
        get('isGlobal')
      )(this.localGlobalCategories),
      categoryId: String.isGuidFormat(this.categoryId) ? this.categoryId : null,
      categoryName: this.category,
      subCategoryId: String.isGuidFormat(this.subCategoryId) ? this.subCategoryId : null,
      subCategoryName: this.subCategory
    };

    return valManager.validate().then((success: any) => {
      if (success) {
        return this.props.actionFunc(data).then((result: any) => {
          if (result.success === false) {
            const msg = result.messages || [];
            const key = head(msg) && head(msg).key;
            key ? valManager.updateErrors(result.messages) : props.onAccept(head(get('messages', head(msg))));
          } else {
            props.onAccept(successMessage);
            return props.onClose && props.onClose(successMessage, result);
          }
        });
      }
    });
  };

  /**
   * Handle when click cancel button
   */
  onCancelClosing = () => {
    this.props.onReject();
  };

  /**
   * Handle when popup closing
   * @param  {Function} closeCallback function
   * @param  {string} type of close button click
   */
  onClosing = (closeCallback: any, type: string) => {
    if (type === ACCEPT_TEXT) {
      this.onAcceptClosing();
    } else if (type === CANCEL_TEXT) {
      this.onCancelClosing();
    }
  };

  /**
   * Handle on change name dashboard
   * @param  {Object} event
   */
  onChangeName = (e: any) => {
    this.name = e.target.value;
  };

  /**
   * On change save into option
   * @param {Object} value
   */
  onChangeCategoryType = action((value: any) => {
    this.selectedSaveInto = value.id;
    this.categoryId = null;
    this.category = null;
    this.subCategoryId = null;
    this.subCategory = null;
  });

  /**
   * Handle when select  category
   * @param  {string} value select  category
   * @param  {string} name  select  category
   */
  onChangeCategory = action((value: string, name: string) => {
    this.categoryId = value;
    this.category = name;
    this.subCategoryId = null;
    this.subCategory = null;
  });

  /**
   * Handle when add new category
   * @param  {object} value new category
   */
  onAddCategory = action((value: any) => {
    this.categories = [value];
    this.subCategories = [];
    return true;
  });

  /**
   * Handle when select sub category
   * @param  {string} value select sub category
   * @param  {string} name  select sub category
   */
  onChangeSubCategory = action((value: string, name: string) => {
    this.subCategoryId = value;
    this.subCategory = name;
  });

  /**
   * Handle when add new sub category
   * @param  {object} value new sub category
   */
  onAddSubCategory = (value: any) => {
    this.subCategories = [value];
    return true;
  };

  /**
   * Get selected category
   */
  getSelectedCategory = () => (isNil(this.categoryId) ? null : { id: this.categoryId, name: this.category });

  /**
   * Get selected sub-category
   */
  getSelectedSubCategory = () =>
    isNil(this.subCategoryId) ? null : { id: this.subCategoryId, name: this.subCategory };

  /**
   * Prevent add new category
   */
  preventAddNew = () => false;

  /**
   * Render name group
   * @param {String} label
   */
  renderNameGroup = (label: string) => (
    <GridLayout.Row>
      <GridLayout.Col cols='3'>
        <Label className='control-label u_textNormal'>{label}</Label>
      </GridLayout.Col>
      <GridLayout.Col cols='9'>
        <ValidatableInput
          value={this.name}
          onChange={this.onChangeName}
          validations={DashboardConstants.CONSTRAINTS.NAME}
          field='name'
          type='text'
          name='name'
          className='form-control'
        />
      </GridLayout.Col>
    </GridLayout.Row>
  );

  /**
   * Render save into group
   * @param {String} label
   */
  renderSaveIntoGroup = (label: string) => (
    <GridLayout.Row>
      <GridLayout.Col cols='3'>
        <Label className='control-label u_textNormal'>{label}</Label>
      </GridLayout.Col>
      <GridLayout.Col cols='9'>
        <Select
          options={this.localGlobalCategories}
          labelKey='name'
          valueKey='id'
          value={this.selectedSaveInto}
          onChange={this.onChangeCategoryType}
          scalable={false}
          textboxMode={false}
        />
      </GridLayout.Col>
    </GridLayout.Row>
  );

  /**
   * Render category group
   * @param {String} label
   */
  renderCategoryGroup = (label, selectedCategory, selectCategoryHint, onAddCategory) => (
    <GridLayout.Row>
      <GridLayout.Col cols='3'>
        <Label className='control-label u_textNormal'>{label}</Label>
      </GridLayout.Col>
      <GridLayout.Col cols='9'>
        <Select
          simpleValue
          options={this.categories}
          labelKey='name'
          valueKey='id'
          value={selectedCategory}
          noResultsText={selectCategoryHint}
          onChange={this.onChangeCategory}
          onAddNew={onAddCategory}
          scalable={false}
          isLazyLoading
          getData={this.getCategories}
          onSearchLazy={this.handleSearchCategoryData}
          onCloseMenu={this.resetCategory}
          isLastPage={this.isLastPageCategorySearch}
        />
      </GridLayout.Col>
    </GridLayout.Row>
  );

  /**
   * Render sub-category group
   * @param {String} label
   */
  renderSubCategoryGroup = (label, selectedSubCategory, selectSubCategoryHint, onAddSubCategory) => (
    <GridLayout.Row>
      <GridLayout.Col cols='3'>
        <Label className='control-label u_textNormal'>{label}</Label>
      </GridLayout.Col>
      <GridLayout.Col cols='9'>
        <Select
          simpleValue
          options={this.subCategories}
          labelKey='name'
          valueKey='id'
          value={selectedSubCategory}
          noResultsText={selectSubCategoryHint}
          onChange={this.onChangeSubCategory}
          onAddNew={onAddSubCategory}
          disabled={this.disableSubCategory}
          scalable={false}
          isLazyLoading
          getData={this.getSubCategories}
          onSearchLazy={this.handleSearchSubCategoryData}
          onCloseMenu={this.resetSubCategory}
          isLastPage={this.isLastPageSubCategorySearch}
        />
      </GridLayout.Col>
    </GridLayout.Row>
  );

  /**
   * Render body
   */
  renderContainer = () => {
    const {
      popupConfig: { DASHBOARD_NAME, SAVEINTO, CATEGORY, SUBCATEGORY },
      hasCanCreateNewCategoryPrivilege,
      action
    } = this.props;
    const isSystemAdmin = uiState.currentUser.systemAdmin;
    const label = String.format(DashboardConstants.MESSAGE.M07, lowerCase(DashboardConstants.ACTION_ENUM[action]));

    let onAddCategory = this.onAddCategory;
    let onAddSubCategory = this.onAddSubCategory;
    let selectCategoryHint = DashboardConstants.SELECT_CATEGORY_HINT;
    let selectSubCategoryHint = DashboardConstants.SELECT_SUB_CATEGORY_HINT;

    if (!hasCanCreateNewCategoryPrivilege) {
      onAddCategory = this.preventAddNew;
      selectCategoryHint = null;
      onAddSubCategory = this.preventAddNew;
      selectSubCategoryHint = null;
    }

    const selectedCategory = this.getSelectedCategory();
    const selectedSubCategory = this.getSelectedSubCategory();

    return (
      <GridLayout className='form-horizontal'>
        <GridLayout.Row>
          <GridLayout.Col cols='12'>{label}</GridLayout.Col>
        </GridLayout.Row>
        {this.renderNameGroup(DASHBOARD_NAME)}
        {isSystemAdmin && isNil(uiState.tenantId) && this.renderSaveIntoGroup(SAVEINTO)}
        {this.renderCategoryGroup(CATEGORY, selectedCategory, selectCategoryHint, onAddCategory)}
        {this.renderSubCategoryGroup(SUBCATEGORY, selectedSubCategory, selectSubCategoryHint, onAddSubCategory)}
      </GridLayout>
    );
  };

  render() {
    const { action, localization } = this.props;
    const popupTitle = `${DashboardConstants.ACTION_ENUM[action]} ${DashboardConstants.DASHBOARD}`;

    return (
      <Popup visible onClosing={this.onClosing} autoClose={false} type='none'>
        <Popup.Header title={popupTitle} />
        <Popup.Body>{this.renderContainer()}</Popup.Body>
        <Popup.Footer>
          <Popup.ConfirmationButton type='accept'>{localization.getText('COMMON_LABEL_OK')}</Popup.ConfirmationButton>
          <Popup.ConfirmationButton type='cancel'>
            {localization.getText('COMMON_LABEL_CANCEL')}
          </Popup.ConfirmationButton>
        </Popup.Footer>
      </Popup>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/SavePopup/Izenda.ActionPopup.js